import type { VosJsonDocument } from './vosjson';

/**
 * The types the frontend works in, and the wire types the API actually speaks.
 *
 * These are two different vocabularies and this file keeps them apart on
 * purpose. The frontend's names are the ones the forms, routes and components
 * were built against ('english', 'association', `keyword_source`); the API's
 * names are the ones `web/api/app/schemas.py` validates ('en',
 * 'association_strength', `author_keywords`/`index_keywords`). They drifted
 * because the two halves were written against a prose spec rather than against
 * each other, and nothing caught it: the old client asserted response types
 * instead of checking them, so every mismatch compiled and then rendered as
 * `undefined`.
 *
 * The rule now: anything ending in `Wire` is the API's shape, byte for byte.
 * Everything else is the frontend's. `lib/api.ts` owns every translation
 * between the two and does it with plain functions and explicit objects --
 * never with `as`.
 */

// ---------------------------------------------------------------------------
// frontend vocabulary
// ---------------------------------------------------------------------------

export type AnalysisKind =
  | 'co_authorship'
  | 'keyword_co_occurrence'
  | 'terms'
  | 'citation'
  | 'bibliographic_coupling'
  | 'co_citation';

export const ANALYSIS_KINDS: readonly AnalysisKind[] = [
  'co_authorship',
  'keyword_co_occurrence',
  'terms',
  'citation',
  'bibliographic_coupling',
  'co_citation',
];

export type CountingMethod = 'full' | 'fractional';
export type KeywordSource = 'author' | 'index' | 'both';
export type TermLanguage = 'english' | 'chinese';
export type CoCitationKey = 'matchkey' | 'string';
export type NormalizationMethod = 'none' | 'association' | 'fractionalization' | 'linlog';

/** Everything the form can set, in the frontend's own names. `lib/api.ts`
 *  flattens this into `AnalysisRequestWire`; two members have no wire
 *  equivalent at all and are documented where they are refused. */
export interface AnalysisParameters {
  // per-analysis
  unit?: string;
  counting?: CountingMethod;
  keyword_source?: KeywordSource;
  min_occurrences?: number;
  min_citations?: number;
  max_items?: number | null;
  fields?: string[];
  language?: TermLanguage;
  binary_counting?: boolean;
  relevance_fraction?: number | null;
  /** No wire equivalent: `AnalysisRequest` has `relevance_fraction` but no
   *  term count. Sending it would be rejected by `extra="forbid"`. */
  n_terms?: number | null;
  /** No wire equivalent either, though `journalmap.pipeline.co_citation` takes
   *  one. The API pins it to the library default. */
  cocitation_key?: CoCitationKey;
  // shared: normalization, clustering, layout
  normalization?: NormalizationMethod;
  resolution?: number;
  min_cluster_size?: number;
  merge_small_clusters?: boolean;
  n_random_starts?: number;
  n_iterations?: number;
  attraction?: number;
  repulsion?: number;
  largest_component?: boolean;
  seed?: number;
}

/**
 * The query builder's structured fields.
 *
 * OpenAlex itself has no such structure: it takes one `filter` expression and
 * one `search` term. `lib/api.ts` compiles these fields into that expression,
 * and the same compiled string is used for the count and for the run, so the
 * number shown is the number of works the job will see.
 */
export interface OpenAlexQuery {
  search?: string;
  year_from?: number | null;
  year_to?: number | null;
  venue?: string;
  open_access?: boolean;
  institution?: string;
  country?: string;
  /** Raw OpenAlex filter syntax, merged with the structured fields. */
  filter?: string;
  max_results?: number;
}

export type JobSource =
  | { kind: 'openalex'; query: OpenAlexQuery }
  | { kind: 'upload'; upload_id: string; filename: string };

export interface JobRequest {
  analysis: AnalysisKind;
  parameters: AnalysisParameters;
  source: JobSource;
  /** Name shown in the saved-maps list. Becomes the API's `title`. */
  name?: string;
}

/** Stage names a progress event may carry. `running` is the only one this
 *  deployment ever emits, and the client emits it: `POST /v1/analyses` is a
 *  single synchronous request that reports nothing until it is finished. The
 *  rest are kept because they are the stages the pipeline really has, and are
 *  what a future streaming endpoint would name. */
export type JobStage =
  | 'queued'
  | 'running'
  | 'fetching'
  | 'parsing'
  | 'extracting'
  | 'segmenting'
  | 'counting'
  | 'thresholding'
  | 'normalizing'
  | 'layout'
  | 'clustering'
  | 'writing'
  | 'done';

export const JOB_STAGES: readonly JobStage[] = [
  'queued',
  'running',
  'fetching',
  'parsing',
  'extracting',
  'segmenting',
  'counting',
  'thresholding',
  'normalizing',
  'layout',
  'clustering',
  'writing',
  'done',
];

/**
 * What happened while the map was built.
 *
 * The API sends `MapBuild.as_dict()`, whose keys are `items`, `links`,
 * `clusters`, `items_counted`, `dropped_by_thresholds`. The names here are the
 * ones the result screen reads. `lib/api.ts` maps between them.
 */
export interface BuildSummary {
  n_items: number;
  n_links: number;
  n_clusters: number;
  total_link_strength: number;
  /** Items counted before thresholds; null when the analysis cannot say. */
  n_items_counted: number | null;
  n_dropped_by_thresholds: number;
  /** CPM quality of the clustering. Null for a degenerate network. */
  clustering_quality: number | null;
  used_leidenalg: boolean;
  notes: string[];
}

export interface JobResult {
  /** There is no job resource on this API -- a run is one request -- so this
   *  is the analysis id, the same value as `map_id`. */
  job_id: string;
  /** The analysis id, and the id every analysis route takes. */
  map_id: string;
  name: string;
  analysis: AnalysisKind;
  /** Only what `AnalysisSummary` reports back -- unit, counting, language. The
   *  request that produced the map is stored server-side in full, fixed seeds
   *  and all, but the API does not publish it. */
  parameters: AnalysisParameters;
  /** Known when this process ran the job; null for a map read back from the
   *  server, which does not report where its corpus came from. */
  source_kind: 'openalex' | 'upload' | null;
  created_at: string;
  /** The round trip as the browser measured it, for a run this process made.
   *  Null on a read: the API publishes no timing of its own. */
  duration_ms: number | null;
  n_documents: number;
  build: BuildSummary;
  /** Diagnostics worth showing above the map: low reference match rate, a
   *  heuristic tagger standing in for spaCy, a map with no links. */
  warnings: string[];
  shared: boolean;
  /** The id a shared map is read by. This API shares a map under its own id,
   *  so this is `map_id` while `shared` is true and null otherwise. */
  share_id: string | null;
  /** VOSviewer JSON, exactly as `journalmap.formats.vosjson` writes it. */
  map: VosJsonDocument;
  /** Signed download URLs for the three artefacts, when the response carried
   *  them. They expire, so they are never stored. */
  artifacts: ArtifactLinks | null;
}

export interface PublicMap {
  share_id: string;
  name: string;
  analysis: AnalysisKind;
  created_at: string;
  n_documents: number;
  build: BuildSummary;
  warnings: string[];
  /** VOSviewer JSON, exactly as `journalmap.formats.vosjson` writes it. */
  map: VosJsonDocument;
}

/** Signed, time-limited artefact URLs. */
export interface ArtifactLinks {
  json_url: string;
  map_url: string;
  network_url: string;
  expires_in_seconds: number;
}

/**
 * One progress event.
 *
 * The API does not stream. It answers `POST /v1/analyses` with a single JSON
 * object, so `accepted` and `error` never arrive over the wire and `result` is
 * the return value rather than an event. `lib/api.ts` emits exactly one
 * `progress` around the request; the union keeps its full shape because the
 * progress panel switches on `type` and because a streaming endpoint is the
 * obvious next step for a deployment that raises the document cap.
 */
export type JobEvent =
  | { type: 'accepted'; job_id: string }
  | { type: 'progress'; stage: string; percent?: number; message?: string }
  | { type: 'result'; result: JobResult }
  | { type: 'error'; error: ApiErrorPayload };

export interface QuotaStatus {
  /** True when this account has no ceiling.
   *
   *  The API signals it by reporting `remaining` as -1. Deriving a boolean here
   *  rather than leaving the sentinel loose is the point: every consumer tests
   *  `remaining <= 0` to decide "out of quota", and -1 satisfies that, so an
   *  exempt account would have been told it had run out and had its run button
   *  disabled — the exact opposite of being exempt. */
  unlimited: boolean;
  /** Runs allowed per window. */
  limit: number;
  used: number;
  remaining: number;
  /** ISO 8601 instant at which `used` goes back to zero. */
  resets_at: string;
  /** Human-readable window name, e.g. "day" or "month". */
  window: string;
  /** The monthly document allowance, which binds sooner than the daily job
   *  count for anyone running large queries. */
  documents: {
    limit: number;
    used: number;
    remaining: number;
    resets_at: string;
  };
}

export interface ServiceLimits {
  max_documents: number;
  /** The API calls this `max_map_items`. It is the map-size cap, and the UI
   *  reads it under the shorter name; see `getLimits`. */
  max_items: number;
  max_upload_bytes: number;
}

export interface UploadReceipt {
  upload_id: string;
  filename: string;
  /** The API calls this `size_bytes`. */
  n_bytes: number;
  /** Format the API sniffed: "wos", "scopus", "ris", "pubmed". */
  format: string;
  /** The API calls this `n_documents`: records it parsed out of the file. */
  n_records: number | null;
  /** Said at upload time, before any analysis: a file whose references barely
   *  match, or one with no abstracts and no keywords. */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// wire vocabulary -- these mirror web/api/app/schemas.py exactly
// ---------------------------------------------------------------------------

/** `AnalysisRequest.language`. Not 'english'/'chinese'. */
export type ApiLanguage = 'en' | 'zh';

/** `AnalysisRequest.normalization`. Not 'association'/'linlog'. */
export type ApiNormalization =
  | 'none'
  | 'association_strength'
  | 'fractionalization'
  | 'linlog_modularity';

/** `TermField`: the only two document fields a term map may read. */
export type ApiTermField = 'title' | 'abstract';

export interface OpenAlexSourceWire {
  kind: 'openalex';
  /** One OpenAlex filter expression, `key:value,key2:value2`. */
  filters?: string | null;
  search?: string | null;
  max_results?: number;
}

export interface UploadSourceWire {
  kind: 'upload';
  upload_id: string;
}

export type AnalysisSourceWire = OpenAlexSourceWire | UploadSourceWire;

/**
 * The body of `POST /v1/analyses`, flat, exactly as pydantic accepts it.
 *
 * `_Strict` sets `extra="forbid"`, so an unknown key is a 422 rather than a
 * silently ignored parameter. That cuts both ways: nothing may be added here
 * that `AnalysisRequest` does not declare, and every optional field left out
 * takes the server's documented default rather than an accidental one.
 */
export interface AnalysisRequestWire {
  source: AnalysisSourceWire;
  analysis: AnalysisKind;

  unit?: string | null;
  counting?: CountingMethod;
  author_keywords?: boolean;
  index_keywords?: boolean;
  fields?: ApiTermField[];
  language?: ApiLanguage;
  binary_counting?: boolean;
  relevance_fraction?: number | null;

  min_occurrences?: number | null;
  min_citations?: number;
  max_items?: number | null;

  normalization?: ApiNormalization;
  resolution?: number;
  min_cluster_size?: number;
  merge_small_clusters?: boolean;
  clustering_random_starts?: number;
  clustering_iterations?: number;
  randomness?: number;
  attraction?: number;
  repulsion?: number;
  layout_random_starts?: number;
  largest_component?: boolean;
  seed?: number;

  title?: string | null;
  shared?: boolean;
}

/**
 * The wire shape of `GET /v1/quota`, exactly as the API sends it.
 *
 * The API tracks two independent quotas -- analyses per day and documents per
 * month -- so its response is not the flat object the header badge wants. This
 * type exists so the difference is written down instead of being assumed: a
 * response cast straight to `QuotaStatus` compiled fine and produced
 * `undefined` for every field the badge reads, so the header rendered the raw
 * template `剩 {remaining} / {limit}` and the "out of quota" banner never fired,
 * because `undefined <= 0` is false.
 */
export interface QuotaWire {
  day: string;
  month: string;
  jobs_used: number;
  jobs_limit: number;
  jobs_remaining: number;
  documents_used: number;
  documents_limit: number;
  documents_remaining: number;
  day_resets_at: string;
  month_resets_at: string;
  max_documents_per_job: number;
  max_items_per_map: number;
}

/**
 * Error codes the app reacts to.
 *
 * The first block comes from the API (`app/errors.py`); the second is minted
 * in the browser by `lib/api.ts` for failures that never reach the server.
 * `describeError` switches on these, and its default branch still says
 * something useful for a code neither side knows yet.
 */
export type ApiErrorCode =
  // from the API
  | 'quota_exceeded'
  | 'rate_limited'
  | 'too_many_documents'
  | 'too_many_items'
  | 'upload_too_large'
  | 'invalid_request'
  | 'parse_failed'
  | 'empty_result'
  | 'empty_file'
  | 'empty_map'
  | 'upstream_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'internal'
  // minted in the browser
  | 'network'
  | 'timeout'
  | 'cancelled'
  | 'not_configured'
  /** A query field the API has no filter for. */
  | 'unsupported_filter'
  /** A form control the API's request model does not expose. */
  | 'unsupported_parameter';

export interface ApiErrorPayload {
  code: ApiErrorCode | string;
  message: string;
  /** Code-specific numbers: `limit`, `actual`, `resets_at`, `retry_after`. */
  details?: Record<string, unknown>;
}

/** Export formats offered on the result screen. */
export type ExportFormat = 'json' | 'map' | 'network' | 'png';
